import React, { useMemo, useState } from 'react';
import { unmappedSubLocations } from '../../utils/locations';

// "New projects found" review step for the Locations page.
//
// The portal sync discovers projects; this is where they become sub-locations.
// Nothing is created without a click, which is the same confirm-before-apply rule
// the portal's device moves already follow — the portal must never be able to add
// rows to the Locations page on its own.
//
// Each row is one portal project. Creating it means two writes:
//   1. the site it will sit within, if that company has no site yet
//   2. portalProjects[pkey].location = that site
// Devices follow on the next portal apply; nothing is moved from here.

const COLS = 'minmax(0,1.4fr) minmax(0,1fr) 90px 80px';

function SubLocationsReview({ st, snapshot, siteNames, onCreate, isReadOnly }) {
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState({});     // pkey -> site name override
  const [skipped, setSkipped] = useState({});   // pkey -> true

  const rows = useMemo(
    () => unmappedSubLocations(snapshot, st.portalProjects, st.portalCompanies),
    [snapshot, st.portalProjects, st.portalCompanies]
  );

  // The site a row would land on: an explicit choice, else the existing site whose
  // name matches the company (case-insensitively, so "SRE" never becomes a second
  // site beside "Sre"), else a new site named after the company.
  const targetFor = row => {
    if (chosen[row.id]) return chosen[row.id];
    const existing = siteNames.find(n => n.toLowerCase() === row.company.trim().toLowerCase());
    return existing || row.company.trim();
  };

  const pending = rows.filter(r => !skipped[r.id] && r.named);
  const unnamed = rows.filter(r => !r.named);

  // Nothing actionable: an unnamed-only list is reported in Settings, not here.
  if (!pending.length) return null;

  const newSites = Array.from(
    new Set(pending.map(targetFor).filter(t => !siteNames.some(n => n.toLowerCase() === t.toLowerCase())))
  );
  const devices = pending.reduce((n, r) => n + r.deviceCount, 0);

  const apply = () => {
    onCreate(pending.map(r => ({
      pkey: r.id, name: r.name, site: targetFor(r),
      companyId: r.companyId, projectId: r.projectId, deviceCount: r.deviceCount,
    })));
    setOpen(false);
  };

  const btn = {
    display: 'inline-flex', alignItems: 'center', height: 30, padding: '0 12px', borderRadius: 6,
    border: '1px solid var(--border-default)', background: 'var(--surface-default)',
    color: 'var(--text-primary)', fontFamily: 'inherit', fontSize: 13, cursor: 'pointer',
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16, borderColor: 'var(--warning-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
        <span aria-hidden="true" style={{ flexShrink: 0, width: 8, height: 8, borderRadius: 9999, background: 'var(--warning-text)' }} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            {pending.length} new {pending.length === 1 ? 'project' : 'projects'} on the OWL portal
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {devices} device{devices === 1 ? '' : 's'} on {pending.length === 1 ? 'it' : 'them'}
            {newSites.length
              ? ` · would add ${newSites.length} new location${newSites.length === 1 ? '' : 's'}`
              : ' · all sit within existing locations'}
            {unnamed.length ? ` · ${unnamed.length} unnamed project${unnamed.length === 1 ? '' : 's'} skipped` : ''}
          </div>
        </div>
        <button type="button" onClick={() => setOpen(o => !o)} style={btn}>
          {open ? 'Hide' : 'Review'}
        </button>
        {!isReadOnly && (
          <button type="button" onClick={apply} className="btn btn-primary" style={{ height: 30 }}>
            Add {pending.length} sub-location{pending.length === 1 ? '' : 's'}
          </button>
        )}
      </div>

      {open && (
        <div style={{ borderTop: '1px solid var(--border-default)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: '0 16px', height: 32, alignItems: 'center', padding: '0 16px', background: 'var(--surface-inset)', fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
            <div>Project</div><div>Goes within</div>
            <div style={{ textAlign: 'right' }}>Devices</div><div />
          </div>
          {rows.map(row => {
            const skip = !!skipped[row.id];
            const target = targetFor(row);
            const isNew = !siteNames.some(n => n.toLowerCase() === target.toLowerCase());
            return (
              <div key={row.id} style={{ display: 'grid', gridTemplateColumns: COLS, gap: '0 16px', alignItems: 'center', minHeight: 44, padding: '6px 16px', borderBottom: '1px solid var(--border-subtle)', opacity: (skip || !row.named) ? 0.5 : 1 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.named
                      ? row.name
                      : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Unnamed · project {row.projectId}</span>}
                  </div>
                  <div className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {row.company} · project {row.projectId}
                  </div>
                </div>
                <div style={{ minWidth: 0 }}>
                  <select
                    value={chosen[row.id] || ''}
                    disabled={skip || !row.named}
                    onChange={e => setChosen(c => ({ ...c, [row.id]: e.target.value }))}
                    aria-label={`Location for ${row.name || 'project ' + row.projectId}`}
                    style={{ width: '100%', height: 30, padding: '0 8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--surface-default)', color: 'var(--text-primary)', fontFamily: 'inherit', fontSize: 13 }}>
                    <option value="">{isNew ? `New location — ${target}` : target}</option>
                    {siteNames.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  {isNew && !chosen[row.id] && (
                    <div style={{ fontSize: 11, color: 'var(--warning-text)', marginTop: 2 }}>Location will be created</div>
                  )}
                </div>
                <div className="tnum" style={{ textAlign: 'right', fontSize: 14, color: 'var(--text-muted)' }}>{row.deviceCount}</div>
                <div style={{ textAlign: 'right' }}>
                  {row.named && (
                    <button type="button" onClick={() => setSkipped(s => ({ ...s, [row.id]: !skip }))} style={{ ...btn, height: 26, padding: '0 8px', fontSize: 12 }}>
                      {skip ? 'Include' : 'Skip'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {!!unnamed.length && (
            <div style={{ padding: '10px 16px', background: 'var(--surface-inset)', fontSize: 12, color: 'var(--text-muted)' }}>
              Unnamed projects are never created — a sub-location called &ldquo;Project 32&rdquo; tells nobody anything.
              Import the portal&rsquo;s project export in Settings &rarr; Portal Sync to name them, then come back here.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SubLocationsReview;
